import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppLayout from '@cloudscape-design/components/app-layout';
import Badge from '@cloudscape-design/components/badge';
import BarChart from '@cloudscape-design/components/bar-chart';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import DateRangePicker, { DateRangePickerProps } from '@cloudscape-design/components/date-range-picker';
import Grid, { GridProps } from '@cloudscape-design/components/grid';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import Modal from '@cloudscape-design/components/modal';
import Pagination from '@cloudscape-design/components/pagination';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table, { TableProps } from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import Toggle from '@cloudscape-design/components/toggle';
import { beginLogin, consumeReturnTo, exchangeCode, getAlias, getIdToken } from '../auth';
import { AppHeader } from '../components/AppHeader';
import { AttachmentView } from '../components/AttachmentView';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { RichText } from '../components/RichText';
import { SideNav } from '../components/SideNav';
import { APP_HEADER_ID } from '../constants';
import { formatTimestamp, orderScopes, scopeLabel } from '../utils/format';
import { withSupport } from '../utils/support';
import { downloadReport } from '../utils/reportDownload';
import {
  getReport,
  listReports,
  NotFoundError,
  ReportDetail,
  ReportSearch,
  ReportSummary,
  UnauthorizedError,
} from '../api';
import { loadCapabilities } from '../capabilities';
import { navigate, reportDetailPath } from '../router';
import { buildSearch } from '../utils/reportSearch';
import { useFillViewportHeight } from '../utils/useFillViewportHeight';
import { ConfirmDiscardModal } from '../components/ConfirmDiscardModal';
import { DeleteReportButton } from '../components/DeleteReportButton';
import { SafeExternalLink } from '../components/SafeExternalLink';
import { ScopeEditor } from '../components/ScopeEditor';
import { getReportsList, saveReportsList } from './reportsListCache';


// Left rail (dashboard) : right (table) = 1 : 2 on >= small, stacked below.
const GRID_DEFINITION: GridProps['gridDefinition'] = [{ colspan: { default: 12, s: 4 } }, { colspan: { default: 12, s: 8 } }];

// Floors for the two fill-height columns so neither collapses to an unusable
// sliver on a short viewport; below these they scroll internally instead.
const LIST_MIN_HEIGHT_PX = 260;
const DASHBOARD_MIN_HEIGHT_PX = 200;

const DATE_RANGE_RELATIVE_OPTIONS: readonly DateRangePickerProps.RelativeOption[] = [
  { key: 'previous-7-days', amount: 7, unit: 'day', type: 'relative' },
  { key: 'previous-30-days', amount: 30, unit: 'day', type: 'relative' },
  { key: 'previous-3-months', amount: 3, unit: 'month', type: 'relative' },
];

const validateDateRange: DateRangePickerProps.ValidationFunction = (value) => {
  if (!value || value.type !== 'absolute') return { valid: true };
  if (!value.startDate || !value.endDate) {
    return { valid: false, errorMessage: 'Select both a start and end date.' };
  }
  if (value.startDate > value.endDate) {
    return { valid: false, errorMessage: 'The start date must be on or before the end date.' };
  }
  return { valid: true };
};

const DATE_RANGE_I18N: DateRangePickerProps.I18nStrings = {
  relativeModeTitle: 'Relative range',
  absoluteModeTitle: 'Absolute range',
  relativeRangeSelectionHeading: 'Choose a range',
  relativeRangeSelectionMonthlyDescription: 'Each month counts from its first day.',
  startDateLabel: 'Start date',
  endDateLabel: 'End date',
  startTimeLabel: 'Start time',
  endTimeLabel: 'End time',
  clearButtonLabel: 'Clear',
  cancelButtonLabel: 'Cancel',
  applyButtonLabel: 'Apply',
  todayAriaLabel: 'Today',
  nextMonthAriaLabel: 'Next month',
  previousMonthAriaLabel: 'Previous month',
  customRelativeRangeOptionLabel: 'Custom range',
  customRelativeRangeOptionDescription: 'Set a custom range',
  customRelativeRangeUnitLabel: 'Unit of time',
  customRelativeRangeDurationLabel: 'Duration',
  formatRelativeRange: (value) => `Last ${value.amount} ${value.unit}${value.amount === 1 ? '' : 's'}`,
  formatUnit: (unit, value) => (value === 1 ? unit : `${unit}s`),
};

type Phase = 'loading' | 'ready' | 'error';
type DetailPhase = 'loading' | 'ready' | 'error';

function formatShort(date: Date): string {
  return `${date.toLocaleString('default', { month: 'short' })} ${date.getDate()}`;
}

function weekRangeLabel(start: Date): string {
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return `${formatShort(start)} – ${formatShort(end)}`;
}

interface WeeklyChartData {
  series: { x: string; y: number }[];
  rangeByLabel: Map<string, string>;
  xDomain: string[];
  yDomain: [number, number];
}

const WEEKS_SHOWN = 4;

function buildWeeklyData(reports: ReportSummary[]): WeeklyChartData {
  const now = new Date();
  const weeks: { start: Date; label: string; range: string }[] = [];
  for (let i = WEEKS_SHOWN - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i * 7);
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
    weeks.push({ start: d, label: formatShort(d), range: weekRangeLabel(d) });
  }

  const counts = new Map<string, number>();
  const rangeByLabel = new Map<string, string>();
  for (const w of weeks) {
    counts.set(w.label, 0);
    rangeByLabel.set(w.label, w.range);
  }

  for (const report of reports) {
    if (!report.publishedAt) continue;
    const pubDate = new Date(report.publishedAt);
    for (let i = weeks.length - 1; i >= 0; i--) {
      if (pubDate >= weeks[i].start) {
        counts.set(weeks[i].label, (counts.get(weeks[i].label) || 0) + 1);
        break;
      }
    }
  }

  const series = weeks.map((w) => ({ x: w.label, y: counts.get(w.label) || 0 }));
  return {
    series,
    rangeByLabel,
    xDomain: series.map((d) => d.x),
    yDomain: [0, Math.max(...series.map((d) => d.y), 1)],
  };
}


export function ReportsPage() {
  const [phase, setPhase] = useState<Phase>('loading');
  const [statusMessage, setStatusMessage] = useState('Loading…');
  const [pages, setPages] = useState<ReportSummary[][]>([]);
  const [pageCursors, setPageCursors] = useState<(string | undefined)[]>([undefined]);
  const [frontierHasNext, setFrontierHasNext] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const [paginationError, setPaginationError] = useState<string | undefined>(undefined);
  const [availableScopes, setAvailableScopes] = useState<string[]>([]);
  const [selectedScope, setSelectedScope] = useState<string | undefined>(undefined);
  const [alias, setAlias] = useState('');
  const [authenticated, setAuthenticated] = useState(false);

  const [queryText, setQueryText] = useState('');
  const [searchContent, setSearchContent] = useState(false);
  const [dateRange, setDateRange] = useState<DateRangePickerProps.Value | null>(null);
  const [activeSearch, setActiveSearch] = useState<ReportSearch | undefined>(undefined);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailPhase, setDetailPhase] = useState<DetailPhase>('loading');
  const [detailMessage, setDetailMessage] = useState('');
  const [detail, setDetail] = useState<ReportDetail | null>(null);

  const [lastRefreshed, setLastRefreshed] = useState<string | undefined>(undefined);

  const [scopeDirty, setScopeDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  const bootstrapped = useRef(false);
  const requestController = useRef<AbortController | null>(null);
  const detailController = useRef<AbortController | null>(null);
  const [listScrollRef, listMaxHeight] = useFillViewportHeight<HTMLDivElement>(
    phase === 'ready',
    LIST_MIN_HEIGHT_PX,
  );
  const [dashboardRef, dashboardMaxHeight] = useFillViewportHeight<HTMLDivElement>(
    phase === 'ready',
    DASHBOARD_MIN_HEIGHT_PX,
  );

  const startRequest = useCallback(() => {
    requestController.current?.abort();
    const controller = new AbortController();
    requestController.current = controller;
    return controller;
  }, []);

  const reports = useMemo(() => pages.flat(), [pages]);
  const currentPageReports = pages[currentPageIndex - 1] ?? [];

  const loadPage = useCallback(
    async (
      scope: string | undefined,
      pageIndex: number,
      cursor: string | undefined,
      signal?: AbortSignal,
      search?: ReportSearch,
      inlineError = false,
    ) => {
      try {
        const page = search
          ? await listReports(scope, cursor, signal, search)
          : await listReports(scope, cursor, signal);
        if (signal?.aborted) return;
        setPages((prev) => {
          const next = prev.slice(0, pageIndex);
          next[pageIndex] = page.reports;
          return next;
        });
        setPageCursors((prev) => {
          const next = prev.slice(0, pageIndex + 1);
          next[pageIndex + 1] = page.nextCursor;
          return next;
        });
        setFrontierHasNext(Boolean(page.nextCursor));
        setCurrentPageIndex(pageIndex + 1);
        setPaginationError(undefined);
        if (page.availableScopes) setAvailableScopes(page.availableScopes);
        if (page.scope) setSelectedScope(page.scope);
        if (pageIndex === 0) setLastRefreshed(new Date().toISOString());
        setPhase('ready');
      } catch (err) {
        if (signal?.aborted) return;
        if (err instanceof UnauthorizedError) {
          await beginLogin();
          return;
        }
        const message = withSupport(err instanceof Error ? err.message : 'Could not load reports.');
        if (pageIndex > 0 || inlineError) {
          setPaginationError(message);
        } else {
          setPhase('error');
          setStatusMessage(message);
        }
      }
    },
    [],
  );

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    void (async () => {
      const params = new URLSearchParams(window.location.search);

      if (params.has('code')) {
        setStatusMessage('Completing sign-in…');
        const code = params.get('code')!;
        window.history.replaceState({}, document.title, '/');
        try {
          await exchangeCode(code);
        } catch (err) {
          setPhase('error');
          setStatusMessage(withSupport(err instanceof Error ? `Sign-in failed: ${err.message}` : 'Sign-in failed.'));
          return;
        }
        const target = consumeReturnTo();
        if (target !== '/') {
          navigate(target, { replace: true });
          return;
        }
      }

      const token = await getIdToken();
      if (!token) {
        await beginLogin();
        return;
      }

      setAlias(getAlias());
      setAuthenticated(true);
      void loadCapabilities().catch(() => {});

      const cached = getReportsList();
      if (cached) {
        setPages(cached.pages);
        setPageCursors(cached.pageCursors);
        setFrontierHasNext(cached.frontierHasNext);
        setCurrentPageIndex(cached.currentPageIndex);
        setSelectedScope(cached.selectedScope);
        setAvailableScopes(cached.availableScopes);
        setLastRefreshed(cached.lastRefreshed);
        if (cached.searchState) {
          setQueryText(cached.searchState.queryText);
          setSearchContent(cached.searchState.searchContent);
          setDateRange(cached.searchState.dateRange);
          setActiveSearch(cached.searchState.active);
        }
        setPhase('ready');
        return;
      }

      const controller = startRequest();
      await loadPage(undefined, 0, undefined, controller.signal);
    })();
  }, [loadPage, startRequest]);

  // Keep the list cache in sync so a later remount can restore it.
  useEffect(() => {
    if (phase === 'ready') {
      saveReportsList({
        pages,
        pageCursors,
        frontierHasNext,
        currentPageIndex,
        selectedScope,
        availableScopes,
        searchState: { queryText, searchContent, dateRange, active: activeSearch },
        lastRefreshed,
      });
    }
  }, [
    phase,
    pages,
    pageCursors,
    frontierHasNext,
    currentPageIndex,
    selectedScope,
    availableScopes,
    queryText,
    searchContent,
    dateRange,
    activeSearch,
    lastRefreshed,
  ]);

  useEffect(
    () => () => {
      requestController.current?.abort();
      detailController.current?.abort();
    },
    [],
  );

  const onPageChange = useCallback(
    async (pageIndex: number) => {
      if (pageIndex <= pages.length) {
        setCurrentPageIndex(pageIndex);
        setPaginationError(undefined);
        return;
      }
      const cursor = pageCursors[pageIndex - 1];
      if (!cursor) return;
      const controller = startRequest();
      setPageLoading(true);
      try {
        await loadPage(selectedScope, pageIndex - 1, cursor, controller.signal, activeSearch);
      } finally {
        if (requestController.current === controller) setPageLoading(false);
      }
    },
    [pages.length, pageCursors, selectedScope, activeSearch, loadPage, startRequest],
  );

  const onScopeChange = useCallback(
    async (scope: string) => {
      if (scope === selectedScope) return;
      const controller = startRequest();
      setSelectedScope(scope);
      // A scope switch starts a fresh, unsearched view of the new partition.
      setQueryText('');
      setSearchContent(false);
      setDateRange(null);
      setActiveSearch(undefined);
      setPages([]);
      setPageCursors([undefined]);
      setFrontierHasNext(false);
      setCurrentPageIndex(1);
      setPaginationError(undefined);
      setPageLoading(false);
      setPhase('loading');
      setStatusMessage('Loading…');
      await loadPage(scope, 0, undefined, controller.signal);
    },
    [selectedScope, loadPage, startRequest],
  );

  const onRefresh = useCallback(async () => {
    const controller = startRequest();
    setPaginationError(undefined);
    setPageLoading(true);
    try {
      await loadPage(selectedScope, 0, undefined, controller.signal, activeSearch, true);
    } finally {
      if (requestController.current === controller) setPageLoading(false);
    }
  }, [selectedScope, activeSearch, loadPage, startRequest]);

  const applySearch = useCallback(
    async (search: ReportSearch | undefined) => {
      const controller = startRequest();
      setActiveSearch(search);
      setPaginationError(undefined);
      setPageLoading(true);
      try {
        await loadPage(selectedScope, 0, undefined, controller.signal, search);
      } finally {
        if (requestController.current === controller) setPageLoading(false);
      }
    },
    [selectedScope, loadPage, startRequest],
  );

  const onQueryChange = useCallback(
    (text: string) => {
      void applySearch(buildSearch(text, searchContent, dateRange));
    },
    [searchContent, dateRange, applySearch],
  );

  const onSearchContentChange = useCallback(
    (checked: boolean) => {
      setSearchContent(checked);
      if (!queryText.trim()) return;
      void applySearch(buildSearch(queryText, checked, dateRange));
    },
    [queryText, dateRange, applySearch],
  );

  const onDateRangeChange = useCallback(
    (range: DateRangePickerProps.Value | null) => {
      setDateRange(range);
      void applySearch(buildSearch(queryText, searchContent, range));
    },
    [queryText, searchContent, applySearch],
  );

  const openReport = useCallback(async (reportId: string) => {
    detailController.current?.abort();
    const controller = new AbortController();
    detailController.current = controller;

    setDetailOpen(true);
    setDetailPhase('loading');
    setDetail(null);
    try {
      const report = await getReport(reportId, controller.signal);
      if (controller.signal.aborted) return;
      setDetail(report);
      setDetailPhase('ready');
    } catch (err) {
      if (controller.signal.aborted) return;
      if (err instanceof UnauthorizedError) {
        await beginLogin();
        return;
      }
      setDetailPhase('error');
      setDetailMessage(
        withSupport(
          err instanceof NotFoundError
            ? 'Report not found, or outside your access scopes.'
            : err instanceof Error
              ? err.message
              : 'Could not load report.',
        ),
      );
    }
  }, []);

  const doCloseDetail = useCallback(() => {
    detailController.current?.abort();
    setConfirmClose(false);
    setScopeDirty(false);
    setDetailOpen(false);
  }, []);

  const closeDetail = useCallback(() => {
    if (scopeDirty) {
      setConfirmClose(true);
      return;
    }
    doCloseDetail();
  }, [scopeDirty, doCloseDetail]);

  const onScopesSaved = useCallback(
    (next: string[]) => {
      setScopeDirty(false);
      setDetail((prev) => (prev ? { ...prev, scopes: next } : prev));
      void onRefresh();
    },
    [onRefresh],
  );

  const onReportDeleted = useCallback(() => {
    doCloseDetail();
    void onRefresh();
  }, [doCloseDetail, onRefresh]);

  const { series: weeklyData, rangeByLabel, xDomain, yDomain } = useMemo(() => buildWeeklyData(reports), [reports]);
  const reportsThisMonth = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    return reports.filter((r) => r.publishedAt && new Date(r.publishedAt) >= monthStart).length;
  }, [reports]);
  const latestReportAt = useMemo(() => {
    let newest: string | undefined;
    for (const r of reports) {
      if (r.publishedAt && (!newest || r.publishedAt > newest)) newest = r.publishedAt;
    }
    return newest;
  }, [reports]);

  const chartSeries = useMemo(
    () => (reports.length === 0 ? [] : [{ title: 'Reports', type: 'bar' as const, data: weeklyData }]),
    [reports.length, weeklyData],
  );
  const renderChartPopoverFooter = useCallback(
    (xValue: string) => (
      <Box color="text-body-secondary" fontSize="body-s">
        {rangeByLabel.get(xValue) || xValue}
      </Box>
    ),
    [rangeByLabel],
  );

  const scopeOptions = useMemo(
    () => orderScopes(availableScopes).map((scope) => ({ label: scopeLabel(scope), value: scope })),
    [availableScopes],
  );

  const columnDefinitions = useMemo<TableProps.ColumnDefinition<ReportSummary>[]>(
    () => [
      {
        id: 'title',
        header: 'Title',
        cell: (item) => (
          <SpaceBetween direction="horizontal" size="xs">
            <Link onFollow={() => openReport(item.reportId)}>{item.title || '(untitled)'}</Link>
            {item.untagged && <Badge color="red">Untagged</Badge>}
          </SpaceBetween>
        ),
        isRowHeader: true,
        maxWidth: 350,
      },
      {
        id: 'publishedAt',
        header: 'Published',
        cell: (item) => formatTimestamp(item.publishedAt),
        width: 200,
      },
    ],
    [openReport],
  );

  const reportsFilter = useMemo(
    () => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
        <div style={{ flex: 1 }}>
          <TextFilter
            filteringText={queryText}
            filteringPlaceholder="Search reports"
            filteringAriaLabel="Search reports"
            onChange={({ detail }) => setQueryText(detail.filteringText)}
            onDelayedChange={({ detail }) => onQueryChange(detail.filteringText)}
            loading={pageLoading}
          />
        </div>
        <DateRangePicker
          value={dateRange}
          onChange={({ detail }) => onDateRangeChange(detail.value)}
          relativeOptions={DATE_RANGE_RELATIVE_OPTIONS}
          isValidRange={validateDateRange}
          i18nStrings={DATE_RANGE_I18N}
          dateOnly
          placeholder="Date range"
          ariaLabel="Filter by published date"
          expandToViewport
        />
        <Toggle checked={searchContent} onChange={({ detail }) => onSearchContentChange(detail.checked)}>
          Search content
        </Toggle>
      </div>
    ),
    [queryText, dateRange, searchContent, pageLoading, onQueryChange, onDateRangeChange, onSearchContentChange],
  );

  const pagination = useMemo(
    () => (
      <Pagination
        currentPageIndex={currentPageIndex}
        pagesCount={Math.max(pages.length, 1)}
        openEnd={frontierHasNext}
        disabled={pageLoading}
        onChange={({ detail: pageDetail }) => {
          void onPageChange(pageDetail.currentPageIndex);
        }}
        ariaLabels={{
          nextPageLabel: 'Next page',
          previousPageLabel: 'Previous page',
          pageLabel: (pageNumber) => `Page ${pageNumber}`,
        }}
      />
    ),
    [currentPageIndex, pages.length, frontierHasNext, pageLoading, onPageChange],
  );

  const onDownload = useCallback(() => {
    if (detail) downloadReport(detail);
  }, [detail]);

  const onViewFullPage = useCallback(() => {
    if (detail) navigate(reportDetailPath(detail.reportId));
  }, [detail]);

  return (
    <>
      <AppHeader alias={alias} authenticated={authenticated} />
      <AppLayout
        headerSelector={`#${APP_HEADER_ID}`}
        toolsHide
        navigation={<SideNav />}
        breadcrumbs={<Breadcrumbs items={[{ text: 'Reports', href: '/' }]} />}
        content={
          <ContentLayout
            header={
              <Header
                variant="h1"
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button
                      iconName="refresh"
                      onClick={() => void onRefresh()}
                      loading={pageLoading}
                      disabled={phase === 'loading'}
                    >
                      Refresh
                    </Button>
                    {availableScopes.length > 0 && (
                      <Select
                        selectedOption={
                          selectedScope ? { label: scopeLabel(selectedScope), value: selectedScope } : null
                        }
                        onChange={({ detail: changeDetail }) => {
                          const value = changeDetail.selectedOption.value;
                          if (value) void onScopeChange(value);
                        }}
                        options={scopeOptions}
                        ariaLabel="Select scope"
                      />
                    )}
                  </SpaceBetween>
                }
              >
                Reports
              </Header>
            }
          >
            {phase === 'loading' && <StatusIndicator type="loading">{statusMessage}</StatusIndicator>}
            {phase === 'error' && <StatusIndicator type="error">{statusMessage}</StatusIndicator>}
            {phase === 'ready' && (
              <Grid gridDefinition={GRID_DEFINITION}>
                <div
                  ref={dashboardRef}
                  style={{ maxHeight: dashboardMaxHeight, minHeight: DASHBOARD_MIN_HEIGHT_PX, overflowY: 'auto' }}
                >
                  <SpaceBetween size="l">
                  <Container header={<Header variant="h2">At a glance</Header>}>
                    <SpaceBetween size="m">
                      <div>
                        <Box variant="awsui-key-label">Scope</Box>
                        <Box data-testid="glance-scope">{selectedScope ? scopeLabel(selectedScope) : '—'}</Box>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Reports this month</Box>
                        <Box variant="awsui-value-large" data-testid="glance-this-month">
                          {reportsThisMonth}
                        </Box>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Latest report</Box>
                        <Box data-testid="glance-latest">
                          {latestReportAt ? formatTimestamp(latestReportAt) : 'None loaded'}
                        </Box>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Last refreshed</Box>
                        <Box data-testid="glance-refreshed">
                          {lastRefreshed ? formatTimestamp(lastRefreshed) : '—'}
                        </Box>
                      </div>
                    </SpaceBetween>
                  </Container>
                  <Container header={<Header variant="h2">Reports: Past Four Weeks</Header>}>
                    <BarChart
                      series={chartSeries}
                      xDomain={xDomain}
                      yDomain={yDomain}
                      detailPopoverFooter={renderChartPopoverFooter}
                      xTitle="Week of"
                      yTitle="Count"
                      hideFilter
                      hideLegend
                      height={200}
                      empty={<Box textAlign="center">No reports loaded yet.</Box>}
                    />
                  </Container>
                  </SpaceBetween>
                </div>

                <SpaceBetween size="m">
                  <div
                    ref={listScrollRef}
                    style={{ maxHeight: listMaxHeight, minHeight: LIST_MIN_HEIGHT_PX, overflowY: 'auto' }}
                  >
                    <Table
                      variant="container"
                      stickyHeader
                      items={currentPageReports}
                      trackBy="reportId"
                      wrapLines
                      loading={pageLoading}
                      loadingText="Loading reports…"
                      empty={
                        <Box textAlign="center">
                          {activeSearch
                            ? 'No reports match your search.'
                            : 'No reports are visible to your access scopes yet.'}
                        </Box>
                      }
                      columnDefinitions={columnDefinitions}
                      filter={reportsFilter}
                      pagination={pagination}
                    />
                  </div>
                  {paginationError && <StatusIndicator type="error">{paginationError}</StatusIndicator>}
                </SpaceBetween>
              </Grid>
            )}
          </ContentLayout>
        }
      />

      <Modal
        visible={detailOpen}
        onDismiss={closeDetail}
        header={detail?.title || 'Report'}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>{detail && <DeleteReportButton reportId={detail.reportId} onDeleted={onReportDeleted} />}</div>
            <SpaceBetween direction="horizontal" size="xs">
              {detail && (
                <Button variant="link" onClick={onViewFullPage}>
                  View full page
                </Button>
              )}
              {detail && (
                <Button iconName="download" onClick={onDownload}>
                  Download
                </Button>
              )}
              <Button variant="primary" onClick={closeDetail}>
                Close
              </Button>
            </SpaceBetween>
          </div>
        }
        size="large"
      >
        {detailPhase === 'loading' && <StatusIndicator type="loading">Loading report…</StatusIndicator>}
        {detailPhase === 'error' && <StatusIndicator type="error">{detailMessage}</StatusIndicator>}
        {detailPhase === 'ready' && detail && (
          <SpaceBetween size="l">
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Published</Box>
                <div>{formatTimestamp(detail.publishedAt)}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Source</Box>
                <div>{detail.source || '—'}</div>
              </div>
            </ColumnLayout>

            {detail.tags && detail.tags.length > 0 && (
              <div>
                <Box variant="awsui-key-label">Tags</Box>
                <div>{detail.tags.join(', ')}</div>
              </div>
            )}

            {detail.contentUrl && (
              <div>
                <Box variant="awsui-key-label">Reference</Box>
                <div>
                  <SafeExternalLink url={detail.contentUrl} />
                </div>
              </div>
            )}

            <ScopeEditor
              key={detail.reportId}
              reportId={detail.reportId}
              scopes={detail.scopes}
              onSaved={onScopesSaved}
              onDirtyChange={setScopeDirty}
            />

            <div>
              <Box variant="awsui-key-label">Body</Box>
              <div>
                <RichText text={detail.body} />
              </div>
            </div>

            {detail.supplementalUrls && detail.supplementalUrls.length > 0 && (
              <div>
                <Box variant="awsui-key-label">Supplemental links</Box>
                {detail.supplementalUrls.map((url, idx) => (
                  <div key={idx}>
                    <SafeExternalLink url={url} />
                  </div>
                ))}
              </div>
            )}

            {detail.attachments && detail.attachments.length > 0 && (
              <div>
                <Box variant="awsui-key-label">Attachments</Box>
                <SpaceBetween size="m">
                  {detail.attachments.map((att, idx) => (
                    <AttachmentView key={att.attachmentId ?? idx} attachment={att} />
                  ))}
                </SpaceBetween>
              </div>
            )}
          </SpaceBetween>
        )}
      </Modal>

      <ConfirmDiscardModal
        visible={confirmClose}
        onKeepEditing={() => setConfirmClose(false)}
        onDiscard={doCloseDetail}
      />
    </>
  );
}
